import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowUpRight, Shield, Thermometer, Eye, ShieldCheck, BadgeCheck, Handshake } from "lucide-react";
import BeforeAfterSlider from "../components/BeforeAfterSlider";

const fmt = (n) => `$${Number(n).toLocaleString()}`;

// Shown if the homepage content can't be loaded, so visitors never get a blank page.
// Mirrors SITE_CONTENT_DEFAULTS in backend/routers/site.py.
const CONTENT_FALLBACK = {
    hero_eyebrow: "A Private Automotive Showroom",
    hero_headline: "Curated vehicles.\nPremium film.\nProfessional tools.",
    hero_subtitle: "Explore hand-selected pre-owned vehicles, premium automotive window film, and professional installation tools curated for drivers, shops, and serious installers.",
    hero_image: "https://images.unsplash.com/photo-1552519507-da3b142c6e3d?w=2400&q=80&auto=format&fit=crop",
    primary_btn_text: "Browse Vehicles", primary_btn_link: "/cars",
    secondary_btn_text: "Explore Window Film", secondary_btn_link: "/automotive-window-film",
    third_btn_text: "Shop Tools", third_btn_link: "/tools",
    featured_vehicle_ids: [],
};

export default function Home() {
    const [vehicles, setVehicles] = useState([]);
    const [vehiclesState, setVehiclesState] = useState("loading"); // loading | ready | error
    const [content, setContent] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            let c = CONTENT_FALLBACK;
            try { c = (await api.get("/site/content")).data || CONTENT_FALLBACK; } catch { /* use fallback */ }
            if (!alive) return;
            setContent(c);
            // Admin-picked featured vehicles (Homepage Content) win; otherwise vehicles flagged "featured".
            const ids = c.featured_vehicle_ids || [];
            try {
                if (ids.length) {
                    const all = (await api.get("/vehicles")).data;
                    const byId = Object.fromEntries(all.map(v => [v.id, v]));
                    if (alive) setVehicles(ids.map(id => byId[id]).filter(Boolean));
                } else {
                    const featured = (await api.get("/vehicles?featured=true")).data;
                    if (alive) setVehicles(featured);
                }
                if (alive) setVehiclesState("ready");
            } catch { if (alive) setVehiclesState("error"); }
        })();
        return () => { alive = false; };
    }, []);

    const c = content || {};
    const headlineLines = (c.hero_headline || "").split("\n").filter(Boolean);
    const btns = [
        ...(c.primary_btn_text ? [{ text: c.primary_btn_text, link: c.primary_btn_link || "/", primary: true, testid: "hero-browse-cars" }] : []),
        ...(c.secondary_btn_text ? [{ text: c.secondary_btn_text, link: c.secondary_btn_link || "/", testid: "hero-explore-tint" }] : []),
        ...(c.third_btn_text ? [{ text: c.third_btn_text, link: c.third_btn_link || "/", testid: "hero-shop-tools" }] : []),
    ];

    if (content === null) return <div data-testid="home-page" className="h-screen bg-charcoal-900" />;

    return (
        <div data-testid="home-page">
            {c.announcement_active && c.announcement_text && (
                <div data-testid="announcement-banner" className="bg-gold text-white text-center text-xs md:text-sm tracking-[0.15em] uppercase py-2.5 px-4">
                    {c.announcement_text}
                </div>
            )}
            {/* HERO */}
            <section className="relative h-[92vh] min-h-[640px] overflow-hidden">
                {c.hero_image && (
                    <img
                        src={c.hero_image}
                        alt="MBG Auto Sales showroom"
                        fetchPriority="high"
                        decoding="async"
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                )}
                <div className="absolute inset-0 hero-overlay" />
                <div className="relative h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24">
                    <div className="max-w-3xl animate-fade-up">
                        {c.hero_eyebrow && (
                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-12 h-px bg-gold" />
                                <span data-testid="hero-eyebrow" className="text-[11px] tracking-[0.4em] uppercase text-gold">{c.hero_eyebrow}</span>
                            </div>
                        )}
                        {headlineLines.length > 0 && (
                            <h1 data-testid="hero-headline" className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter font-medium leading-[0.95]">
                                {headlineLines.map((line, i) => (
                                    <React.Fragment key={i}>
                                        {i === 1 ? <em className="text-gold font-normal not-italic">{line}</em> : line}
                                        {i < headlineLines.length - 1 && <br />}
                                    </React.Fragment>
                                ))}
                            </h1>
                        )}
                        {c.hero_subtitle && (
                            <p data-testid="hero-subtitle" className="mt-8 text-[#33302C] max-w-xl text-lg font-light leading-relaxed">
                                {c.hero_subtitle}
                            </p>
                        )}
                        {btns.length > 0 && (
                            <div className="mt-10 flex flex-wrap gap-4">
                                {btns.map((b) => (
                                    <Link key={b.testid} data-testid={b.testid} to={b.link}
                                        className={b.primary
                                            ? "px-8 py-4 bg-gold text-white font-semibold uppercase tracking-[0.2em] text-xs hover:bg-gold-hover transition-colors inline-flex items-center gap-2"
                                            : "px-8 py-4 border border-[#8C5A3C]/40 text-[#1A1A1A] uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors"}>
                                        {b.text} {b.primary && <ArrowUpRight size={16} />}
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* TRUST STRIP */}
            <section data-testid="trust-strip" className="border-b border-[#E2D4C1] bg-[#F7F3EB]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-[#E2D4C1]">
                    {[
                        { icon: ShieldCheck, title: "Vetted Vehicles", desc: "Every car is hand-inspected and history-verified before it reaches our showroom.", testid: "trust-vetted" },
                        { icon: BadgeCheck, title: "Lifetime Tint Warranty", desc: "Premium films backed by a lifetime guarantee against fading, bubbling, and peeling.", testid: "trust-warranty" },
                        { icon: Handshake, title: "B2B Approved Network", desc: "Trusted wholesale pricing and installer support for approved professional partners.", testid: "trust-b2b" },
                    ].map((t) => (
                        <div key={t.testid} data-testid={t.testid} className="flex items-start gap-4 py-7 md:py-8 md:px-8 first:md:pl-0 last:md:pr-0">
                            <span className="shrink-0 w-11 h-11 border border-gold/40 grid place-items-center text-gold">
                                <t.icon size={18} strokeWidth={1.6} />
                            </span>
                            <div>
                                <div className="font-display text-lg tracking-tight mb-1">{t.title}</div>
                                <p className="text-[13px] leading-relaxed text-[#6B645C]">{t.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                    <div>
                        <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">The Collection</div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-tight">Featured Vehicles</h2>
                    </div>
                    <Link to="/cars" data-testid="view-all-cars" className="text-sm uppercase tracking-[0.2em] text-[#33302C] hover:text-gold inline-flex items-center gap-2">View All <ArrowUpRight size={14} /></Link>
                </div>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {vehiclesState === "loading" && [0, 1, 2].map((k) => (
                        <div key={k} aria-hidden="true" className="bg-charcoal-800 border border-[#E2D4C1] animate-pulse">
                            <div className="aspect-[4/3] bg-[#E2D4C1]" />
                            <div className="p-6 space-y-3"><div className="h-3 w-1/3 bg-[#E2D4C1]" /><div className="h-6 w-2/3 bg-[#E2D4C1]" /></div>
                        </div>
                    ))}
                    {vehiclesState !== "loading" && vehicles.length === 0 && (
                        <div data-testid="featured-empty" className="md:col-span-2 lg:col-span-3 border border-[#E2D4C1] bg-[#F7F3EB] py-16 px-6 text-center">
                            <p className="text-[#33302C] mb-6">
                                {vehiclesState === "error" ? "We couldn't load the collection just now." : "New arrivals are being prepared for the showroom."}
                            </p>
                            <div className="flex flex-wrap justify-center gap-3">
                                <Link to="/cars" className="px-6 py-3 bg-gold text-white uppercase tracking-[0.2em] text-xs font-semibold hover:bg-gold-hover">See All Vehicles</Link>
                                <Link to="/contact?topic=vehicle" className="px-6 py-3 border border-[#8C5A3C]/40 uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold">Ask About a Vehicle</Link>
                            </div>
                        </div>
                    )}
                    {vehicles.map((v, i) => (
                        <Link
                            key={v.id}
                            to={`/cars/${v.id}`}
                            data-testid={`featured-vehicle-${i}`}
                            className="group block bg-charcoal-800 border border-[#E2D4C1] hover:border-gold/40 transition-all duration-500"
                        >
                            <div className="aspect-[4/3] overflow-hidden bg-[#E2D4C1]">
                                {v.image ? (
                                    <img src={v.image} alt={`${v.year} ${v.make} ${v.model}`} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                ) : null}
                            </div>
                            <div className="p-6">
                                <div className="text-[10px] tracking-[0.3em] uppercase text-[#6B645C] mb-2">{v.year} · {v.body_type}</div>
                                <div className="font-display text-2xl mb-1">{v.make}</div>
                                <div className="text-[#33302C] mb-4">{v.model}</div>
                                <div className="flex items-center justify-between pt-4 border-t border-[#E2D4C1]">
                                    <span className="text-gold font-medium">{fmt(v.price)}</span>
                                    <span className="text-xs text-[#6B645C]">{Number(v.mileage).toLocaleString()} mi</span>
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            </section>

            {/* TINT & SERVICES */}
            <section className="relative py-24 md:py-32 bg-charcoal-800 border-y border-[#E2D4C1]">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center">
                    <div>
                        <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Window Tint & Installation</div>
                        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-6">Films that perform.<br />Tools that deliver.</h2>
                        <p className="text-[#33302C] mb-8 leading-relaxed font-light">From flagship ceramic films with 99% UV rejection to the precise squeegees and heat guns trusted in pro shops — everything you need, in one curated catalog.</p>
                        <div className="flex flex-wrap gap-4">
                            <Link to="/automotive-window-film" data-testid="cta-tint" className="px-8 py-4 bg-gold text-white font-semibold uppercase tracking-[0.2em] text-xs hover:bg-gold-hover transition-colors">Shop Tint</Link>
                            <Link to="/tools" data-testid="cta-tools" className="px-8 py-4 border border-[#8C5A3C]/40 text-[#1A1A1A] uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors">Shop Tools</Link>
                            <Link to="/contact" data-testid="cta-quote" className="px-8 py-4 border border-[#8C5A3C]/40 text-[#1A1A1A] uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors">Request a Quote</Link>
                        </div>
                    </div>
                    <div className="relative aspect-[4/5] overflow-hidden">
                        <img src="https://images.unsplash.com/photo-1708805282706-f44730b7e527?w=1200&q=80&auto=format&fit=crop" alt="Professional tint installation" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-charcoal-800 via-transparent to-transparent" />
                    </div>
                </div>
            </section>

            {/* SEE THE DIFFERENCE — before/after tint showcase */}
            <section data-testid="see-the-difference" className="relative py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-12">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">Tint Preview</div>
                    <h2 className="font-display text-4xl md:text-6xl tracking-tight mb-6">See the Difference</h2>
                    <p className="text-[#33302C] leading-relaxed font-light text-lg">
                        From a cleaner look to better privacy, heat rejection, and UV protection, our tint upgrades transform the way your vehicle looks, feels, and protects your interior.
                    </p>
                    <p data-testid="tint-preview-disclaimer" className="mt-4 text-xs text-[#6B645C]">
                        Illustrative preview: drag each slider to see a simulated tint effect on sample photography. Actual results depend on the film and shade you choose.
                    </p>
                </div>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[
                        {
                            title: "Front Side Windows",
                            desc: "Cleaner exterior styling, improved privacy, and reduced glare for a more refined driving experience.",
                            image: "https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1400",
                            icon: Eye, accent: "Privacy · Glare control"
                        },
                        {
                            title: "Full Side Profile",
                            desc: "A balanced tint upgrade across all windows for a consistent, luxury finish that elevates the entire vehicle.",
                            image: "https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?w=1400",
                            icon: Shield, accent: "Uniform · Premium finish"
                        },
                        {
                            title: "Rear Glass Upgrade",
                            desc: "Enhanced sun protection, better cabin comfort, and a darker rear finish for a more premium look.",
                            image: "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1400",
                            icon: Thermometer, accent: "UV · Heat rejection"
                        },
                    ].map((c, i) => (
                        <div
                            key={c.title}
                            data-testid={`transformation-card-${i}`}
                            className="group bg-charcoal-800/70 backdrop-blur border border-[#E2D4C1] hover:border-gold/40 rounded-md overflow-hidden shadow-[0_25px_60px_-30px_rgba(0,0,0,0.9)] transition-all duration-500"
                        >
                            <BeforeAfterSlider image={c.image} alt={c.title} testId={`ba-slider-${i}`} />
                            <div className="p-6 md:p-7">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="w-9 h-9 border border-gold/40 grid place-items-center text-gold"><c.icon size={15} /></span>
                                    <span className="text-[10px] tracking-[0.3em] uppercase text-gold/90">{c.accent}</span>
                                </div>
                                <h3 className="font-display text-2xl mb-3">{c.title}</h3>
                                <p className="text-[#33302C] text-sm leading-relaxed">{c.desc}</p>
                            </div>
                        </div>
                    ))}
                </div>

                {/* CTA block */}
                <div className="mt-16 border border-gold/30 bg-gradient-to-br from-[#F7F3EB] to-[#E2D4C1] p-10 md:p-14 text-center relative overflow-hidden">
                    <div aria-hidden className="absolute -top-32 -right-32 w-80 h-80 rounded-full bg-gold/10 blur-3xl pointer-events-none" />
                    <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3 relative">Premium Tint Service</div>
                    <h3 className="font-display text-3xl md:text-5xl tracking-tight mb-4 relative">Ready to upgrade your vehicle&apos;s look and comfort?</h3>
                    <p className="text-[#33302C] max-w-2xl mx-auto mb-8 relative">
                        Request a tint quote today and get a premium finish matched to your vehicle, privacy goals, and local tint regulations.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-4 relative">
                        <Link
                            data-testid="diff-cta-quote"
                            to="/contact"
                            className="px-8 py-4 bg-gold text-white font-semibold uppercase tracking-[0.2em] text-xs hover:bg-gold-hover transition-colors inline-flex items-center gap-2"
                        >
                            Request Tint Quote <ArrowUpRight size={14} />
                        </Link>
                        <Link
                            data-testid="diff-cta-packages"
                            to="/automotive-window-film"
                            className="px-8 py-4 border border-[#8C5A3C]/40 text-[#1A1A1A] uppercase tracking-[0.2em] text-xs hover:border-gold hover:text-gold transition-colors"
                        >
                            View Tint Packages
                        </Link>
                    </div>
                </div>
            </section>

            {/* B2B BANNER */}
            <section className="py-20 max-w-7xl mx-auto px-6 md:px-12">
                <div className="border border-gold/20 bg-charcoal-800/50 p-10 md:p-16 grid md:grid-cols-3 gap-8 items-center">
                    <div className="md:col-span-2">
                        <div className="text-[11px] tracking-[0.4em] uppercase text-gold mb-3">For Professional Installers</div>
                        <h3 className="font-display text-3xl md:text-4xl tracking-tight mb-3">B2B / Wholesale Program</h3>
                        <p className="text-[#33302C] max-w-xl">Approved wholesale partners unlock dedicated pricing, quote-based purchasing, and direct access to our installer support team.</p>
                    </div>
                    <div className="flex md:justify-end">
                        <Link to="/register?b2b=1" data-testid="b2b-cta" className="px-8 py-4 bg-gold text-white font-semibold uppercase tracking-[0.2em] text-xs hover:bg-gold-hover transition-colors">Apply for B2B</Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
